import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Clock, Smartphone, Package, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_ICON = "frontend/src/assets/LOKKER PRINCIPAL.pnghttps://customer-assets.emergentagent.com/job_secure-parcels/artifacts/9xfoqgv2_LOKKER%20PRINCIPAL.png";

export const HeroSection = () => {
  return (
    <section className="relative min-h-screen bg-lokker-blue pt-20 lg:pt-0 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFFFFF' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        
        {/* Floating Logo Icons */}
        <motion.div
          animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-[10%] w-16 h-16 opacity-10"
        >
          <img src={LOGO_ICON} alt="" className="w-full h-full" />
        </motion.div>
        <motion.div
          animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/3 right-[15%] w-24 h-24 opacity-10"
        >
          <img src={LOGO_ICON} alt="" className="w-full h-full" />
        </motion.div>
        <motion.div
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 left-[20%] w-12 h-12 opacity-5"
        >
          <img src={LOGO_ICON} alt="" className="w-full h-full" />
        </motion.div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 lg:py-0 w-full">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-lokker-lime px-4 py-2 rounded-sm"
            >
              <span className="w-2 h-2 bg-lokker-blue rounded-full animate-pulse" />
              <span className="text-sm font-bold text-black uppercase tracking-wide">
                + de 10.000 encomendas entregues
              </span>
            </motion.div>

            {/* Headline */}
            <div className="space-y-4">
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.1]">
                Receba suas encomendas{' '}
                <span className="text-lokker-lime">quando quiser</span>
              </h1>
              <p className="text-lg sm:text-xl text-white/80 leading-relaxed max-w-xl">
                Armários inteligentes para recebimento e retirada facilitada de encomendas. 
                Praticidade, segurança e autonomia no seu dia a dia.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link to="/planos">
                <Button
                  data-testid="hero-cta-primary"
                  className="bg-lokker-lime text-black font-bold px-8 py-6 text-base rounded-sm flex items-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.3)] hover:translate-y-[2px] transition-all"
                >
                  Começar Agora
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/planos">
                <Button
                  data-testid="hero-cta-planos"
                  variant="outline"
                  className="px-8 py-6 text-base rounded-sm border-2 border-white/30 text-white hover:border-lokker-lime hover:text-lokker-lime bg-transparent"
                >
                  Conheça os Planos
                </Button>
              </Link>
            </div>

            {/* Secondary CTAs */}
            <div className="flex flex-wrap gap-6 pt-4">
              <Link
                to="/empresas"
                data-testid="hero-link-empresas"
                className="text-sm font-medium text-white/70 hover:text-lokker-lime transition-colors flex items-center gap-1"
              >
                Para Empresas
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/condominios"
                data-testid="hero-link-condominios"
                className="text-sm font-medium text-white/70 hover:text-lokker-lime transition-colors flex items-center gap-1"
              >
                Para Condomínios
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10">
              <div>
                <p className="font-heading font-bold text-3xl sm:text-4xl text-lokker-lime">500+</p>
                <p className="text-sm text-white/60">Lockers ativos</p>
              </div>
              <div>
                <p className="font-heading font-bold text-3xl sm:text-4xl text-lokker-lime">50+</p>
                <p className="text-sm text-white/60">Cidades</p>
              </div>
              <div>
                <p className="font-heading font-bold text-3xl sm:text-4xl text-lokker-lime">99%</p>
                <p className="text-sm text-white/60">Satisfação</p>
              </div>
            </div>
          </motion.div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            {/* Main Visual Card */}
            <div className="relative">
              {/* Decorative Frame */}
              <motion.div 
                animate={{ rotate: [3, 5, 3] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -inset-4 border-4 border-lokker-lime rounded-sm"
              />
              
              {/* Main Card */}
              <div className="relative bg-white rounded-sm overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="bg-lokker-blue p-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img src={LOGO_ICON} alt="Lokker" className="w-12 h-12" />
                    <div>
                      <p className="text-lokker-lime font-heading font-bold text-lg">Sua encomenda chegou!</p>
                      <p className="text-white/70 text-sm">Agora mesmo</p>
                    </div>
                  </div>
                  <Smartphone className="w-8 h-8 text-lokker-lime" />
                </div>
                
                {/* Content */}
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-sm">
                    <div className="w-12 h-12 bg-lokker-lime rounded-sm flex items-center justify-center">
                      <Package className="w-6 h-6 text-black" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">Pacote #12847</p>
                      <p className="text-sm text-slate-500">Locker Via Verde Shopping</p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-lokker-blue/5 rounded-sm text-center">
                      <p className="text-2xl font-heading font-bold text-lokker-blue">A-12</p>
                      <p className="text-xs text-slate-500 mt-1">Compartimento</p>
                    </div>
                    <div className="p-4 bg-lokker-lime/20 rounded-sm text-center">
                      <p className="text-2xl font-heading font-bold text-slate-900">7834</p>
                      <p className="text-xs text-slate-500 mt-1">Código de Acesso</p>
                    </div>
                  </div>
                  
                  <button className="w-full bg-lokker-blue text-white font-bold py-4 rounded-sm hover:bg-lokker-blue-dark transition-colors">
                    Abrir Locker
                  </button>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="absolute -right-4 top-1/4 hidden lg:block"
            >
              <div className="bg-white p-4 rounded-sm shadow-lg border-l-4 border-lokker-lime flex items-center gap-3">
                <Shield className="w-6 h-6 text-lokker-blue" />
                <span className="font-medium text-slate-900">100% Seguro</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="absolute -left-4 bottom-1/4 hidden lg:block"
            >
              <div className="bg-white p-4 rounded-sm shadow-lg border-l-4 border-lokker-lime flex items-center gap-3">
                <Clock className="w-6 h-6 text-lokker-blue" />
                <span className="font-medium text-slate-900">24/7 Disponível</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator 
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:block"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs text-white/50 uppercase tracking-wider">Scroll</span>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-lokker-lime rounded-full mt-2"
            />
          </div>
        </div>
      </motion.div>*/}
    </section>
  );
};

export default HeroSection;
